import { useState } from 'react';

export default function FormSection() {
  const [formData, setFormData] = useState({
    businessName: '',
    businessType: '',
    website: '',
    phone: '',
    email: '',
    currentProcess: '',
    challenges: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6cuq9rv53dtlm7j8b30', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          businessName: '',
          businessType: '',
          website: '',
          phone: '',
          email: '',
          currentProcess: '',
          challenges: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 px-6 lg:px-12 bg-gray-50" id="automation-plan-form">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-lg text-gray-700">
            Tell us about your business so we can design it around
            <br />
            your workflow.
          </p>
        </div>

        <form 
          onSubmit={handleSubmit} 
          className="bg-white rounded-lg shadow-sm p-8 lg:p-12"
          data-readdy-form
          id="get-started-form"
        >
          <div className="space-y-6">
            {/* Business Name */}
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-900 mb-2">
                Business Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                placeholder="Your business name"
              />
            </div>

            {/* Business Type */}
            <div>
              <label htmlFor="businessType" className="block text-sm font-medium text-gray-900 mb-2">
                Type of Business <span className="text-red-500">*</span>
              </label>
              <select
                id="businessType"
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent cursor-pointer"
              >
                <option value="">Select your business type</option>
                <option value="contractor">Contractor</option>
                <option value="hvac">HVAC</option>
                <option value="plumbing">Plumbing</option>
                <option value="electrical">Electrical</option>
                <option value="handyman">Handyman</option>
                <option value="cleaning">Cleaning Services</option>
                <option value="power-washing">Power Washing</option>
                <option value="lawn-care">Lawn Care</option>
                <option value="property-maintenance">Property Maintenance</option>
                <option value="repair">Repair Services</option>
                <option value="installer">Installation Services</option>
                <option value="other">Other Service Business</option>
              </select>
            </div>

            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-900 mb-2">
                Website URL
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                placeholder="https://yourbusiness.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                placeholder="(555) 123-4567"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                placeholder="you@yourbusiness.com"
              />
            </div>

            {/* Current Process */}
            <div>
              <label htmlFor="currentProcess" className="block text-sm font-medium text-gray-900 mb-2">
                How do customers currently contact you? <span className="text-red-500">*</span>
              </label>
              <textarea
                id="currentProcess"
                name="currentProcess"
                value={formData.currentProcess}
                onChange={handleChange}
                required
                maxLength={500}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent resize-none"
                placeholder="Example: Contact form on website, phone calls, text messages..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.currentProcess.length}/500 characters
              </p>
            </div>

            {/* Challenges */}
            <div>
              <label htmlFor="challenges" className="block text-sm font-medium text-gray-900 mb-2">
                What's your biggest challenge with responding to inquiries?
              </label>
              <textarea
                id="challenges"
                name="challenges"
                value={formData.challenges}
                onChange={handleChange}
                maxLength={500}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent resize-none"
                placeholder="Example: I'm often on job sites and can't respond quickly..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.challenges.length}/500 characters
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Get My Free Automation Plan'}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md text-sm">
                <strong>Success!</strong> We've received your information and will be in touch soon with your custom automation plan.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm">
                <strong>Error:</strong> Something went wrong. Please try again or contact us directly.
              </div>
            )}

            {/* Disclaimer */}
            <p className="text-xs text-gray-500 text-center">
              By submitting this form, you agree to receive communication from Automigo about your automation plan. 
              We'll never share your information or use it for unrelated marketing.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
